import { Component, type ErrorInfo, type ReactNode } from "react";
import type { AppLocale } from "../i18n/format";

/**
 * The last line of defence against a white screen.
 *
 * WHY THIS EXISTS. React 19 unmounts the entire tree when a render throws, so
 * one bad property access anywhere produces a blank page and no explanation —
 * which is exactly what happened on a real phone after a few ordinary
 * interactions while 215 tests and eight gates were passing. A converter that
 * shows nothing is worse than one that shows a stale number: the promise is
 * "open it, type, get the number", and a white page fails it completely.
 *
 * The fallback deliberately offers RECOVERY, not an apology. Reloading is what
 * the user was forced to do by hand; the button does it for them. The saved
 * session survives a reload — that is the whole point of storing it — so
 * "reload" here costs the user nothing but the tap.
 *
 * DETAILS ARE SHOWN, NOT SWALLOWED. A crash the user can read is a crash they
 * can report. It is also the only signal available from a phone that is not
 * plugged into a laptop.
 */

interface Props {
  readonly locale: AppLocale;
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
  readonly stack: string | null;
}

/** Keeps the last crash where a Playwright soak run can read it back. */
declare global {
  interface Window {
    __lastCrash?: { message: string; stack: string; componentStack: string };
  }
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    /*
     * console.error rather than a silent capture: this is the only channel a
     * phone has, and Safari's remote inspector reads it. No third-party
     * reporting service — the project forbids them, and a crash reporter would
     * be user data leaving the browser.
     */
    console.error("[3aam] render crashed", error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
    window.__lastCrash = {
      message: error.message,
      stack: error.stack ?? "",
      componentStack: info.componentStack ?? "",
    };
  }

  override render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    const ar = this.props.locale === "ar";
    return (
      <div className="crash" role="alert">
        <h1 className="crash__title">{ar ? "تعطّل شيء ما" : "Something broke"}</h1>
        <p className="crash__body">
          {ar
            ? "عملاتك ومبلغك محفوظة. أعد التحميل للمتابعة من حيث توقّفت."
            : "Your currencies and amount are saved. Reload to pick up where you left off."}
        </p>
        <button
          type="button"
          className="crash__action"
          onClick={() => window.location.reload()}
        >
          {ar ? "إعادة التحميل" : "Reload"}
        </button>
        {/* Collapsed by default: useful to anyone reporting it, invisible to
            anyone who just wants their converter back. */}
        <details className="crash__details">
          <summary>{ar ? "تفاصيل تقنية" : "Technical details"}</summary>
          <pre className="crash__pre" dir="ltr">
            {error.message}
            {"\n"}
            {error.stack}
            {stack ? `\n--- component stack ---${stack}` : ""}
          </pre>
        </details>
      </div>
    );
  }
}
